import React, { useState, useEffect } from 'react'
import { API_BASE_URL } from '../config/api'
import axios from 'axios'
import { Card } from 'primereact/card'
import { Button } from 'primereact/button'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { Tag } from 'primereact/tag'
import { Dialog } from 'primereact/dialog'
import { InputNumber } from 'primereact/inputnumber'
import { Calendar } from 'primereact/calendar'
import { Dropdown } from 'primereact/dropdown'
import { InputText } from 'primereact/inputtext'
import { InputTextarea } from 'primereact/inputtextarea'
import { Toast } from 'primereact/toast'
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog'
import { SelectButton } from 'primereact/selectbutton'
import { Skeleton } from 'primereact/skeleton'
import { useAuth } from '../contexts/AuthContext'
import { BankImportWizard } from './BankImportWizard'
import {
  Payment,
  PaymentSummary,
  PaymentMethod,
  PaymentType,
  Member,
  OutgoingPayment,
  OutgoingPaymentCategory,
  OutgoingPaymentStatus,
  CreateOutgoingPaymentDto,
  OutgoingPaymentSummary
} from 'shared'

type PaymentViewType = 'incoming' | 'outgoing'

const PaymentDashboard: React.FC = () => {
  const [paymentView, setPaymentView] = useState<PaymentViewType>('incoming')
  const [payments, setPayments] = useState<Payment[]>([])
  const [outgoingPayments, setOutgoingPayments] = useState<OutgoingPayment[]>([])
  const [summary, setSummary] = useState<PaymentSummary | null>(null)
  const [outgoingSummary, setOutgoingSummary] = useState<OutgoingPaymentSummary | null>(null)
  const [currentYearIncoming, setCurrentYearIncoming] = useState<PaymentSummary | null>(null)
  const [currentYearOutgoing, setCurrentYearOutgoing] = useState<OutgoingPaymentSummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [balanceLoading, setBalanceLoading] = useState(true)
  const [selectedYear, setSelectedYear] = useState<number | null>(null)
  const [showPaymentDialog, setShowPaymentDialog] = useState(false)
  const [editingPayment, setEditingPayment] = useState<Payment | null>(null)
  const [editingOutgoingPayment, setEditingOutgoingPayment] = useState<OutgoingPayment | null>(null)
  const [members, setMembers] = useState<Member[]>([])
  const [paymentFormData, setPaymentFormData] = useState({
    memberId: null as string | null,
    amount: 0,
    paymentYear: new Date().getFullYear() as number | null,
    paymentType: PaymentType.MEMBERSHIP_FEE,
    receiveDate: new Date(),
    paymentMethod: PaymentMethod.BANK_TRANSFER,
    notes: ''
    // Note: reference is auto-generated by backend
  })
  const [outgoingPaymentFormData, setOutgoingPaymentFormData] = useState({
    payee: '',
    amount: 0,
    category: OutgoingPaymentCategory.OTHER,
    paymentDate: new Date(),
    paymentMethod: PaymentMethod.BANK_TRANSFER,
    invoiceNumber: '',
    description: '',
    status: OutgoingPaymentStatus.PENDING
  })
  const [showBankImport, setShowBankImport] = useState(false)
  const toast = React.useRef<Toast>(null)
  const { logout } = useAuth()


  const yearOptions = [
    { label: 'All Years', value: null },
    ...Array.from({ length: 10 }, (_, i) => ({
      label: (new Date().getFullYear() - 5 + i).toString(),
      value: new Date().getFullYear() - 5 + i
    }))
  ]

  const paymentMethodOptions = [
    { label: 'Cash', value: PaymentMethod.CASH },
    { label: 'Bank Transfer', value: PaymentMethod.BANK_TRANSFER },
    { label: 'Credit Card', value: PaymentMethod.CREDIT_CARD },
    { label: 'PayPal', value: PaymentMethod.PAYPAL },
    { label: 'SEPA Debit', value: PaymentMethod.SEPA_DEBIT },
    { label: 'Other', value: PaymentMethod.OTHER }
  ]

  const paymentTypeOptions = [
    { label: 'Membership Fee', value: PaymentType.MEMBERSHIP_FEE },
    { label: 'Donation', value: PaymentType.DONATION },
    { label: 'Event Fee', value: PaymentType.EVENT_FEE },
    { label: 'Other', value: PaymentType.OTHER }
  ]

  const outgoingCategoryOptions = [
    { label: 'Rent', value: OutgoingPaymentCategory.RENT },
    { label: 'Utilities', value: OutgoingPaymentCategory.UTILITIES },
    { label: 'Equipment', value: OutgoingPaymentCategory.EQUIPMENT },
    { label: 'Supplies', value: OutgoingPaymentCategory.SUPPLIES },
    { label: 'Insurance', value: OutgoingPaymentCategory.INSURANCE },
    { label: 'Salary', value: OutgoingPaymentCategory.SALARY },
    { label: 'Contractor', value: OutgoingPaymentCategory.CONTRACTOR },
    { label: 'Marketing', value: OutgoingPaymentCategory.MARKETING },
    { label: 'Maintenance', value: OutgoingPaymentCategory.MAINTENANCE },
    { label: 'Travel', value: OutgoingPaymentCategory.TRAVEL },
    { label: 'Fees', value: OutgoingPaymentCategory.FEES },
    { label: 'Other', value: OutgoingPaymentCategory.OTHER }
  ]

  const outgoingStatusOptions = [
    { label: 'Pending', value: OutgoingPaymentStatus.PENDING },
    { label: 'Paid', value: OutgoingPaymentStatus.PAID },
    { label: 'Cancelled', value: OutgoingPaymentStatus.CANCELLED },
    { label: 'Refunded', value: OutgoingPaymentStatus.REFUNDED }
  ]

  const viewOptions = [
    { label: 'Incoming', value: 'incoming' },
    { label: 'Outgoing', value: 'outgoing' }
  ]

  useEffect(() => {
    const abortController = new AbortController()
    const signal = abortController.signal

    const loadAll = async () => {
      setLoading(true)
      setBalanceLoading(true)

      const currentYear = new Date().getFullYear()
      const viewMatchesCurrentYear = selectedYear === currentYear

      try {
        // Fetch table data + summary for current view
        let incomingSummaryData: PaymentSummary | null = null
        let outgoingSummaryData: OutgoingPaymentSummary | null = null

        if (paymentView === 'incoming') {
          const params = new URLSearchParams()
          if (selectedYear) {
            params.append('paymentYear', selectedYear.toString())
          }

          const [paymentsRes, summaryRes] = await Promise.all([
            axios.get(`${API_BASE_URL}/api/payments?${params.toString()}`, { signal }),
            axios.get(`${API_BASE_URL}/api/payments/summary${selectedYear ? `?year=${selectedYear}` : ''}`, { signal })
          ])
          setPayments(paymentsRes.data.data?.payments || [])
          incomingSummaryData = summaryRes.data.data?.summary || null
          setSummary(incomingSummaryData)
        } else {
          const params = new URLSearchParams()
          if (selectedYear) {
            params.append('year', selectedYear.toString())
          }

          const [outgoingRes, outgoingSummaryRes] = await Promise.all([
            axios.get(`${API_BASE_URL}/api/outgoing-payments?${params.toString()}`, { signal }),
            axios.get(`${API_BASE_URL}/api/outgoing-payments/summary${selectedYear ? `?year=${selectedYear}` : ''}`, { signal })
          ])
          setOutgoingPayments(outgoingRes.data.data?.payments || [])
          outgoingSummaryData = outgoingSummaryRes.data.data?.summary || null
          setOutgoingSummary(outgoingSummaryData)
        }

        setLoading(false)

        // Fetch balance overview — reuse data if selectedYear matches currentYear
        const balancePromises: Promise<void>[] = []

        if (viewMatchesCurrentYear && paymentView === 'incoming') {
          setCurrentYearIncoming(incomingSummaryData)
        } else {
          balancePromises.push(
            axios.get(`${API_BASE_URL}/api/payments/summary?year=${currentYear}`, { signal })
              .then(res => setCurrentYearIncoming(res.data.data?.summary || null))
          )
        }

        if (viewMatchesCurrentYear && paymentView === 'outgoing') {
          setCurrentYearOutgoing(outgoingSummaryData)
        } else {
          balancePromises.push(
            axios.get(`${API_BASE_URL}/api/outgoing-payments/summary?year=${currentYear}`, { signal })
              .then(res => setCurrentYearOutgoing(res.data.data?.summary || null))
          )
        }

        if (paymentView === 'incoming') {
          balancePromises.push(
            axios.get(`${API_BASE_URL}/api/members`, { signal })
              .then(res => setMembers(res.data.data?.members || []))
          )
        }

        await Promise.all(balancePromises)
      } catch (err: any) {
        if (axios.isCancel(err)) return
        console.error('Failed to fetch payment data:', err)
        if (err.response?.status === 401) {
          logout()
        } else {
          toast.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load payment data',
            life: 5000
          })
        }
      } finally {
        if (!signal.aborted) {
          setLoading(false)
          setBalanceLoading(false)
        }
      }
    }

    loadAll()
    return () => abortController.abort()
  }, [selectedYear, paymentView])

  const fetchData = async () => {
    try {
      setLoading(true)

      if (paymentView === 'incoming') {
        const params = new URLSearchParams()
        if (selectedYear) {
          params.append('paymentYear', selectedYear.toString())
        }

        const paymentsRes = await axios.get(`${API_BASE_URL}/api/payments?${params.toString()}`)
        setPayments(paymentsRes.data.data?.payments || [])

        const summaryRes = await axios.get(`${API_BASE_URL}/api/payments/summary${selectedYear ? `?year=${selectedYear}` : ''}`)
        setSummary(summaryRes.data.data?.summary || null)
      } else {
        const params = new URLSearchParams()
        if (selectedYear) {
          params.append('year', selectedYear.toString())
        }

        const outgoingRes = await axios.get(`${API_BASE_URL}/api/outgoing-payments?${params.toString()}`)
        setOutgoingPayments(outgoingRes.data.data?.payments || [])

        const outgoingSummaryRes = await axios.get(`${API_BASE_URL}/api/outgoing-payments/summary${selectedYear ? `?year=${selectedYear}` : ''}`)
        setOutgoingSummary(outgoingSummaryRes.data.data?.summary || null)
      }
    } catch (err: any) {
      console.error('Failed to fetch payment data:', err)
      if (err.response?.status === 401) {
        logout()
      } else {
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load payment data',
          life: 5000
        })
      }
    } finally {
      setLoading(false)
    }
  }

  const openNewPaymentDialog = () => {
    if (paymentView === 'incoming') {
      setEditingPayment(null)
      setEditingOutgoingPayment(null)
      setPaymentFormData({
        memberId: null,
        amount: 12.00,
        paymentYear: new Date().getFullYear(),
        paymentType: PaymentType.MEMBERSHIP_FEE,
        receiveDate: new Date(),
        paymentMethod: PaymentMethod.BANK_TRANSFER,
        notes: ''
      })
    } else {
      setEditingPayment(null)
      setEditingOutgoingPayment(null)
      setOutgoingPaymentFormData({
        payee: '',
        amount: 0,
        category: OutgoingPaymentCategory.OTHER,
        paymentDate: new Date(),
        paymentMethod: PaymentMethod.BANK_TRANSFER,
        invoiceNumber: '',
        description: '',
        status: OutgoingPaymentStatus.PENDING
      })
    }
    setShowPaymentDialog(true)
  }

  const openEditPaymentDialog = (payment: Payment) => {
    setEditingPayment(payment)
    setEditingOutgoingPayment(null)
    setPaymentFormData({
      memberId: payment.memberId || null,
      amount: Number(payment.amount),
      paymentYear: payment.paymentYear || null,
      paymentType: payment.paymentType,
      receiveDate: new Date(payment.receiveDate),
      paymentMethod: payment.paymentMethod,
      notes: payment.notes || ''
    })
    setShowPaymentDialog(true)
  }

  const openEditOutgoingPaymentDialog = (payment: OutgoingPayment) => {
    setEditingPayment(null)
    setEditingOutgoingPayment(payment)
    setOutgoingPaymentFormData({
      payee: payment.payee,
      amount: Number(payment.amount),
      category: payment.category,
      paymentDate: new Date(payment.paymentDate),
      paymentMethod: payment.paymentMethod,
      invoiceNumber: payment.invoiceNumber || '',
      description: payment.description || '',
      status: payment.status
    })
    setShowPaymentDialog(true)
  }

  const handleSavePayment = async () => {
    try {
      if (paymentView === 'incoming') {
        // Validation for incoming payments
        if (paymentFormData.amount <= 0) {
          toast.current?.show({
            severity: 'warn',
            summary: 'Warning',
            detail: 'Please enter a valid amount',
            life: 3000
          })
          return
        }

        // For membership fees, require member and year
        if (paymentFormData.paymentType === PaymentType.MEMBERSHIP_FEE) {
          if (!paymentFormData.memberId) {
            toast.current?.show({
              severity: 'warn',
              summary: 'Warning',
              detail: 'Member is required for membership fees',
              life: 3000
            })
            return
          }
          if (!paymentFormData.paymentYear) {
            toast.current?.show({
              severity: 'warn',
              summary: 'Warning',
              detail: 'Payment year is required for membership fees',
              life: 3000
            })
            return
          }
        }

        const payload = {
          memberId: paymentFormData.memberId,
          amount: Number(paymentFormData.amount),
          paymentYear: paymentFormData.paymentYear,
          paymentType: paymentFormData.paymentType,
          receiveDate: paymentFormData.receiveDate.toISOString(),
          paymentMethod: paymentFormData.paymentMethod,
          notes: paymentFormData.notes || undefined
        }

        if (editingPayment) {
          await axios.put(`${API_BASE_URL}/api/payments/${editingPayment.id}`, payload)
          toast.current?.show({
            severity: 'success',
            summary: 'Success',
            detail: 'Payment updated successfully',
            life: 3000
          })
        } else {
          await axios.post(`${API_BASE_URL}/api/payments`, payload)
          toast.current?.show({
            severity: 'success',
            summary: 'Success',
            detail: 'Payment recorded successfully',
            life: 3000
          })
        }
      } else {
        // Validation for outgoing payments
        if (outgoingPaymentFormData.amount <= 0) {
          toast.current?.show({
            severity: 'warn',
            summary: 'Warning',
            detail: 'Please enter a valid amount',
            life: 3000
          })
          return
        }

        if (!outgoingPaymentFormData.payee.trim()) {
          toast.current?.show({
            severity: 'warn',
            summary: 'Warning',
            detail: 'Payee is required',
            life: 3000
          })
          return
        }

        const payload = {
          payee: outgoingPaymentFormData.payee,
          amount: Number(outgoingPaymentFormData.amount),
          category: outgoingPaymentFormData.category,
          paymentDate: outgoingPaymentFormData.paymentDate.toISOString(),
          paymentMethod: outgoingPaymentFormData.paymentMethod,
          invoiceNumber: outgoingPaymentFormData.invoiceNumber || undefined,
          description: outgoingPaymentFormData.description || undefined,
          status: outgoingPaymentFormData.status
        }

        if (editingOutgoingPayment) {
          await axios.put(`${API_BASE_URL}/api/outgoing-payments/${editingOutgoingPayment.id}`, payload)
          toast.current?.show({
            severity: 'success',
            summary: 'Success',
            detail: 'Outgoing payment updated successfully',
            life: 3000
          })
        } else {
          await axios.post(`${API_BASE_URL}/api/outgoing-payments`, payload)
          toast.current?.show({
            severity: 'success',
            summary: 'Success',
            detail: 'Outgoing payment recorded successfully',
            life: 3000
          })
        }
      }

      setShowPaymentDialog(false)
      setEditingPayment(null)
      setEditingOutgoingPayment(null)
      await fetchData()
    } catch (err: any) {
      console.error('Failed to save payment:', err)
      const errorMessage = err.response?.data?.message ||
                          err.response?.data?.errors?.[0]?.message ||
                          'Failed to save payment'
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: errorMessage,
        life: 5000
      })
    }
  }

  const handleDeletePayment = async (payment: Payment) => {
    confirmDialog({
      message: `Are you sure you want to delete this payment${payment.member ? ` for ${payment.member.firstName} ${payment.member.lastName}` : ''}? This action cannot be undone.`,
      header: 'Delete Payment',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: async () => {
        try {
          await axios.delete(`${API_BASE_URL}/api/payments/${payment.id}`)

          toast.current?.show({
            severity: 'success',
            summary: 'Success',
            detail: 'Payment deleted successfully',
            life: 3000
          })

          await fetchData()
        } catch (err: any) {
          console.error('Failed to delete payment:', err)
          toast.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: err.response?.data?.message || 'Failed to delete payment',
            life: 5000
          })
        }
      }
    })
  }

  const handleDeleteOutgoingPayment = async (payment: OutgoingPayment) => {
    confirmDialog({
      message: `Are you sure you want to delete this payment to ${payment.payee}? This action cannot be undone.`,
      header: 'Delete Outgoing Payment',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: async () => {
        try {
          await axios.delete(`${API_BASE_URL}/api/outgoing-payments/${payment.id}`)

          toast.current?.show({
            severity: 'success',
            summary: 'Success',
            detail: 'Outgoing payment deleted successfully',
            life: 3000
          })

          await fetchData()
        } catch (err: any) {
          console.error('Failed to delete outgoing payment:', err)
          toast.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: err.response?.data?.message || 'Failed to delete outgoing payment',
            life: 5000
          })
        }
      }
    })
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE')
  }

  const memberBodyTemplate = (payment: Payment) => {
    if (!payment.member) {
      return <span className="text-gray-400 italic">No member</span>
    }
    return (
      <div>
        <div className="font-semibold">{payment.member.firstName} {payment.member.lastName}</div>
        <div className="text-sm text-gray-500">#{payment.member.memberNumber}</div>
      </div>
    )
  }

  const amountBodyTemplate = (payment: Payment) => {
    return <span className="font-semibold">{formatCurrency(Number(payment.amount))}</span>
  }

  const typeBodyTemplate = (payment: Payment) => {
    const getColor = (type: PaymentType) => {
      switch (type) {
        case PaymentType.MEMBERSHIP_FEE: return 'info'
        case PaymentType.DONATION: return 'success'
        case PaymentType.EVENT_FEE: return 'warning'
        default: return 'secondary'
      }
    }

    const getLabel = (type: PaymentType) => {
      switch (type) {
        case PaymentType.MEMBERSHIP_FEE: return 'Membership'
        case PaymentType.DONATION: return 'Donation'
        case PaymentType.EVENT_FEE: return 'Event'
        case PaymentType.OTHER: return 'Other'
        default: return type
      }
    }

    return <Tag value={getLabel(payment.paymentType)} severity={getColor(payment.paymentType)} />
  }

  const actionsBodyTemplate = (payment: Payment) => {
    return (
      <div className="flex gap-1">
        <Button
          icon="pi pi-pencil"
          className="p-button-rounded p-button-text p-button-info p-button-sm"
          tooltip="Edit"
          tooltipOptions={{ position: 'top' }}
          onClick={() => openEditPaymentDialog(payment)}
        />
        <Button
          icon="pi pi-trash"
          className="p-button-rounded p-button-text p-button-danger p-button-sm"
          tooltip="Delete"
          tooltipOptions={{ position: 'top' }}
          onClick={() => handleDeletePayment(payment)}
        />
      </div>
    )
  }

  // Outgoing payment body templates
  const categoryBodyTemplate = (payment: OutgoingPayment) => {
    const getColor = (category: OutgoingPaymentCategory) => {
      switch (category) {
        case OutgoingPaymentCategory.RENT: return 'danger'
        case OutgoingPaymentCategory.UTILITIES: return 'warning'
        case OutgoingPaymentCategory.EQUIPMENT: return 'info'
        case OutgoingPaymentCategory.SALARY: return 'danger'
        case OutgoingPaymentCategory.INSURANCE: return 'warning'
        default: return 'secondary'
      }
    }

    return <Tag value={payment.category} severity={getColor(payment.category)} />
  }

  const statusBodyTemplate = (payment: OutgoingPayment) => {
    const getColor = (status: OutgoingPaymentStatus) => {
      switch (status) {
        case OutgoingPaymentStatus.PAID: return 'success'
        case OutgoingPaymentStatus.PENDING: return 'warning'
        case OutgoingPaymentStatus.CANCELLED: return 'danger'
        case OutgoingPaymentStatus.REFUNDED: return 'info'
        default: return 'secondary'
      }
    }

    return <Tag value={payment.status} severity={getColor(payment.status)} />
  }

  const outgoingActionsBodyTemplate = (payment: OutgoingPayment) => {
    return (
      <div className="flex gap-1">
        <Button
          icon="pi pi-pencil"
          className="p-button-rounded p-button-text p-button-info p-button-sm"
          tooltip="Edit"
          tooltipOptions={{ position: 'top' }}
          onClick={() => openEditOutgoingPaymentDialog(payment)}
        />
        <Button
          icon="pi pi-trash"
          className="p-button-rounded p-button-text p-button-danger p-button-sm"
          tooltip="Delete"
          tooltipOptions={{ position: 'top' }}
          onClick={() => handleDeleteOutgoingPayment(payment)}
        />
      </div>
    )
  }

  return (
    <div>
      <Toast ref={toast} />
      <ConfirmDialog />

      {/* Current Year Balance Overview */}
      <div className="mb-6" data-component="balance-overview">
        <h2 className="text-xl font-bold text-gray-800 mb-3 hidden md:block">
          {new Date().getFullYear()} Balance Overview
        </h2>
        <h2 className="text-base font-bold text-gray-800 mb-2 md:hidden">
          {new Date().getFullYear()} Balance
        </h2>

        {balanceLoading ? (
          <>
            {/* Compact skeleton */}
            <div className="block md:hidden">
              <Card>
                <div className="grid grid-cols-3 gap-x-4">
                  {[0, 1, 2].map((i) => (
                    <div key={i} className="flex flex-col items-center gap-2">
                      <Skeleton width="4rem" height="0.75rem" />
                      <Skeleton width="5rem" height="1.5rem" />
                      <Skeleton width="3rem" height="0.625rem" />
                    </div>
                  ))}
                </div>
              </Card>
            </div>
            {/* Full skeleton */}
            <div className="hidden md:grid grid-cols-3 gap-4">
              {[0, 1, 2].map((i) => (
                <Card key={i}>
                  <div className="flex flex-col items-center gap-2">
                    <Skeleton width="6rem" height="0.875rem" />
                    <Skeleton width="8rem" height="2.25rem" />
                    <Skeleton width="4rem" height="0.75rem" />
                  </div>
                </Card>
              ))}
            </div>
          </>
        ) : currentYearIncoming && currentYearOutgoing && (() => {
          const netBalance = currentYearIncoming.totalAmount - currentYearOutgoing.totalAmount;
          const isPositive = netBalance >= 0;
          return (
            <>
              {/* Compact view */}
              <div className="block md:hidden">
                <Card>
                  <div className="grid grid-cols-3 gap-x-4">
                    <div className="flex flex-col items-center text-center">
                      <div className="flex items-center gap-1.5 mb-1">
                        <span className="inline-block w-2 h-2 rounded-full bg-green-500" />
                        <span className="text-xs font-medium text-gray-500">Incoming</span>
                      </div>
                      <span className="text-lg font-bold text-green-600">{formatCurrency(currentYearIncoming.totalAmount)}</span>
                      <span className="text-xs text-gray-400">{currentYearIncoming.totalPayments} payments</span>
                    </div>
                    <div className="flex flex-col items-center text-center">
                      <div className="flex items-center gap-1.5 mb-1">
                        <span className="inline-block w-2 h-2 rounded-full bg-red-500" />
                        <span className="text-xs font-medium text-gray-500">Outgoing</span>
                      </div>
                      <span className="text-lg font-bold text-red-600">{formatCurrency(currentYearOutgoing.totalAmount)}</span>
                      <span className="text-xs text-gray-400">{currentYearOutgoing.totalPayments} payments</span>
                    </div>
                    <div className="flex flex-col items-center text-center">
                      <div className="flex items-center gap-1.5 mb-1">
                        <span className={`inline-block w-2 h-2 rounded-full ${isPositive ? 'bg-blue-500' : 'bg-orange-500'}`} />
                        <span className="text-xs font-medium text-gray-500">Net</span>
                      </div>
                      <span className={`text-lg font-bold ${isPositive ? 'text-blue-600' : 'text-orange-600'}`}>{formatCurrency(netBalance)}</span>
                      <span className="text-xs text-gray-400">{isPositive ? 'Surplus' : 'Deficit'}</span>
                    </div>
                  </div>
                </Card>
              </div>

              {/* Full card view */}
              <div className="hidden md:grid grid-cols-3 gap-4">
                <Card className="bg-green-50 border-green-200">
                  <div className="text-center">
                    <div className="text-sm text-gray-600 mb-1">Total Incoming</div>
                    <div className="text-3xl font-bold text-green-600">
                      {formatCurrency(currentYearIncoming.totalAmount)}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {currentYearIncoming.totalPayments} payments
                    </div>
                  </div>
                </Card>
                <Card className="bg-red-50 border-red-200">
                  <div className="text-center">
                    <div className="text-sm text-gray-600 mb-1">Total Outgoing</div>
                    <div className="text-3xl font-bold text-red-600">
                      {formatCurrency(currentYearOutgoing.totalAmount)}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {currentYearOutgoing.totalPayments} payments
                    </div>
                  </div>
                </Card>
                <Card className={`${isPositive ? 'bg-blue-50 border-blue-200' : 'bg-orange-50 border-orange-200'}`}>
                  <div className="text-center">
                    <div className="text-sm text-gray-600 mb-1">Net Balance</div>
                    <div className={`text-3xl font-bold ${isPositive ? 'text-blue-600' : 'text-orange-600'}`}>
                      {formatCurrency(netBalance)}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {isPositive ? 'Surplus' : 'Deficit'}
                    </div>
                  </div>
                </Card>
              </div>
            </>
          );
        })()}
      </div>

      {/* View Toggle */}
      <div className="mb-4 flex justify-center">
        <SelectButton
          value={paymentView}
          options={viewOptions}
          onChange={(e) => setPaymentView(e.value)}
        />
      </div>

      {/* Summary Cards - Incoming */}
      {paymentView === 'incoming' && (
        <div className="mb-6" data-component="incoming-summary">
          {loading ? (
            <>
              {/* Compact skeleton */}
              <div className="block md:hidden">
                <Card>
                  <div className="grid grid-cols-4 gap-x-2">
                    {[0, 1, 2, 3].map((i) => (
                      <div key={i} className="flex flex-col items-center gap-2">
                        <Skeleton width="3rem" height="0.75rem" />
                        <Skeleton width="2rem" height="1.5rem" />
                      </div>
                    ))}
                  </div>
                </Card>
              </div>
              {/* Full skeleton */}
              <div className="hidden md:grid grid-cols-4 gap-4">
                {[0, 1, 2, 3].map((i) => (
                  <Card key={i}>
                    <div className="flex flex-col items-center gap-2">
                      <Skeleton width="3rem" height="1.75rem" />
                      <Skeleton width="6rem" height="0.875rem" />
                    </div>
                  </Card>
                ))}
              </div>
            </>
          ) : summary && (
            <>
              {/* Compact */}
              <div className="block md:hidden">
                <Card>
                  <div className="grid grid-cols-4 gap-x-2">
                    {[
                      { label: 'Total', value: summary.totalPayments, color: 'text-blue-600', dotColor: 'bg-blue-500', sub: formatCurrency(summary.totalAmount) },
                      { label: 'Fees', value: summary.membershipFees, color: 'text-green-600', dotColor: 'bg-green-500' },
                      { label: 'Donations', value: summary.donations, color: 'text-orange-600', dotColor: 'bg-orange-500' },
                      { label: 'Other', value: summary.otherPayments, color: 'text-purple-600', dotColor: 'bg-purple-500' },
                    ].map((item) => (
                      <div key={item.label} className="flex flex-col items-center text-center">
                        <div className="flex items-center gap-1 mb-1">
                          <span className={`inline-block w-2 h-2 rounded-full ${item.dotColor}`} />
                          <span className="text-xs font-medium text-gray-500">{item.label}</span>
                        </div>
                        <span className={`text-lg font-bold ${item.color}`}>{item.value}</span>
                        {item.sub && <span className="text-xs text-gray-400">{item.sub}</span>}
                      </div>
                    ))}
                  </div>
                </Card>
              </div>
              {/* Full */}
              <div className="hidden md:grid grid-cols-4 gap-4">
                <Card>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">{summary.totalPayments}</div>
                    <div className="text-sm text-gray-600">Total Payments</div>
                    <div className="text-xs text-gray-500">{formatCurrency(summary.totalAmount)}</div>
                  </div>
                </Card>
                <Card>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">{summary.membershipFees}</div>
                    <div className="text-sm text-gray-600">Membership Fees</div>
                  </div>
                </Card>
                <Card>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-orange-600">{summary.donations}</div>
                    <div className="text-sm text-gray-600">Donations</div>
                  </div>
                </Card>
                <Card>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-purple-600">{summary.otherPayments}</div>
                    <div className="text-sm text-gray-600">Other Payments</div>
                  </div>
                </Card>
              </div>
            </>
          )}
        </div>
      )}

      {/* Summary Cards - Outgoing */}
      {paymentView === 'outgoing' && (
        <div className="mb-6" data-component="outgoing-summary">
          {loading ? (
            <>
              <div className="block md:hidden">
                <Card>
                  <div className="grid grid-cols-3 gap-x-2">
                    {[0, 1, 2].map((i) => (
                      <div key={i} className="flex flex-col items-center gap-2">
                        <Skeleton width="3rem" height="0.75rem" />
                        <Skeleton width="2rem" height="1.5rem" />
                      </div>
                    ))}
                  </div>
                </Card>
              </div>
              <div className="hidden md:grid grid-cols-3 gap-4">
                {[0, 1, 2].map((i) => (
                  <Card key={i}>
                    <div className="flex flex-col items-center gap-2">
                      <Skeleton width="3rem" height="1.75rem" />
                      <Skeleton width="6rem" height="0.875rem" />
                    </div>
                  </Card>
                ))}
              </div>
            </>
          ) : outgoingSummary && (
            <>
              {/* Compact */}
              <div className="block md:hidden">
                <Card>
                  <div className="grid grid-cols-3 gap-x-2">
                    {[
                      { label: 'Total', value: outgoingSummary.totalPayments, color: 'text-red-600', dotColor: 'bg-red-500', sub: formatCurrency(outgoingSummary.totalAmount) },
                      { label: 'Pending', value: outgoingSummary.byStatus[OutgoingPaymentStatus.PENDING] || 0, color: 'text-orange-600', dotColor: 'bg-orange-500' },
                      { label: 'Paid', value: outgoingSummary.byStatus[OutgoingPaymentStatus.PAID] || 0, color: 'text-green-600', dotColor: 'bg-green-500' },
                    ].map((item) => (
                      <div key={item.label} className="flex flex-col items-center text-center">
                        <div className="flex items-center gap-1 mb-1">
                          <span className={`inline-block w-2 h-2 rounded-full ${item.dotColor}`} />
                          <span className="text-xs font-medium text-gray-500">{item.label}</span>
                        </div>
                        <span className={`text-lg font-bold ${item.color}`}>{item.value}</span>
                        {item.sub && <span className="text-xs text-gray-400">{item.sub}</span>}
                      </div>
                    ))}
                  </div>
                </Card>
              </div>
              {/* Full */}
              <div className="hidden md:grid grid-cols-3 gap-4">
                <Card>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-red-600">{outgoingSummary.totalPayments}</div>
                    <div className="text-sm text-gray-600">Total Payments</div>
                    <div className="text-xs text-gray-500">{formatCurrency(outgoingSummary.totalAmount)}</div>
                  </div>
                </Card>
                <Card>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-orange-600">
                      {outgoingSummary.byStatus[OutgoingPaymentStatus.PENDING] || 0}
                    </div>
                    <div className="text-sm text-gray-600">Pending</div>
                  </div>
                </Card>
                <Card>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">
                      {outgoingSummary.byStatus[OutgoingPaymentStatus.PAID] || 0}
                    </div>
                    <div className="text-sm text-gray-600">Paid</div>
                  </div>
                </Card>
              </div>
            </>
          )}
        </div>
      )}

      {/* Main Controls */}
      <Card>
        <div className="flex flex-wrap gap-1 items-center mb-2">
          <Dropdown
            value={selectedYear}
            options={yearOptions}
            onChange={(e) => setSelectedYear(e.value)}
            optionLabel="label"
            optionValue="value"
            placeholder="Year"
            className="p-inputtext-sm"
            style={{ width: '6rem' }}
          />
          <Button
            icon="pi pi-plus"
            onClick={openNewPaymentDialog}
            className="p-button-success p-button-sm"
            tooltip={paymentView === 'incoming' ? 'Record Payment' : 'Record Expense'}
            tooltipOptions={{ position: 'bottom' }}
          />
          {paymentView === 'incoming' && (
            <Button
              icon="pi pi-file-import"
              onClick={() => setShowBankImport(true)}
              className="p-button-outlined p-button-sm"
              tooltip="Import Bank CSV"
              tooltipOptions={{ position: 'bottom' }}
            />
          )}
          <Button
            icon="pi pi-refresh"
            onClick={fetchData}
            loading={loading}
            className="p-button-outlined p-button-sm"
            tooltip="Refresh"
            tooltipOptions={{ position: 'bottom' }}
          />
        </div>

        {/* Incoming Payments Table */}
        {paymentView === 'incoming' && (
          <DataTable
            value={payments}
            loading={loading}
            paginator
            rows={20}
            rowsPerPageOptions={[10, 20, 50, 100]}
            className="p-datatable-sm"
            emptyMessage="No payments found"
            showGridlines
            stripedRows
            scrollable
            scrollHeight="600px"
          >
            <Column
              header="Actions"
              body={actionsBodyTemplate}
              style={{ minWidth: '70px', width: 'auto' }}
            />
            <Column
              field="member"
              header="Member"
              body={memberBodyTemplate}
              sortable
            />
            <Column
              field="amount"
              header="Amount"
              body={amountBodyTemplate}
              sortable
            />
            <Column
              field="paymentType"
              header="Type"
              body={typeBodyTemplate}
              sortable
            />
            <Column
              field="receiveDate"
              header="Date"
              body={(payment) => formatDate(payment.receiveDate)}
              sortable
            />
            <Column
              field="paymentYear"
              header="Year"
              body={(payment) => payment.paymentYear || '-'}
              sortable
            />
            <Column
              field="notes"
              header="Notes"
              body={(payment) => payment.notes || '-'}
            />
            <Column
              field="paymentMethod"
              header="Method"
              body={(payment) => payment.paymentMethod || '-'}
            />
            <Column
              field="reference"
              header="Ref"
              body={(payment) => payment.reference || '-'}
            />
          </DataTable>
        )}

        {/* Outgoing Payments Table */}
        {paymentView === 'outgoing' && (
          <DataTable
            value={outgoingPayments}
            loading={loading}
            paginator
            rows={20}
            rowsPerPageOptions={[10, 20, 50, 100]}
            className="p-datatable-sm"
            emptyMessage="No outgoing payments found"
            showGridlines
            stripedRows
            scrollable
            scrollHeight="600px"
          >
            <Column
              header="Actions"
              body={outgoingActionsBodyTemplate}
              style={{ minWidth: '70px', width: 'auto' }}
            />
            <Column
              field="payee"
              header="Payee"
              sortable
            />
            <Column
              field="amount"
              header="Amount"
              body={(payment) => <span className="font-semibold">{formatCurrency(Number(payment.amount))}</span>}
              sortable
            />
            <Column
              field="status"
              header="Status"
              body={statusBodyTemplate}
              sortable
            />
            <Column
              field="category"
              header="Category"
              body={categoryBodyTemplate}
              sortable
            />
            <Column
              field="paymentDate"
              header="Date"
              body={(payment) => formatDate(payment.paymentDate)}
              sortable
            />
            <Column
              field="description"
              header="Description"
              body={(payment) => payment.description || '-'}
            />
            <Column
              field="paymentMethod"
              header="Method"
              sortable
            />
            <Column
              field="invoiceNumber"
              header="Inv #"
              body={(payment) => payment.invoiceNumber || '-'}
            />
          </DataTable>
        )}
      </Card>

      {/* Payment Dialog */}
      <Dialog
        header={
          paymentView === 'incoming'
            ? (editingPayment ? 'Edit Incoming Payment' : 'Record New Incoming Payment')
            : (editingOutgoingPayment ? 'Edit Outgoing Payment' : 'Record New Outgoing Payment')
        }
        visible={showPaymentDialog}
        style={{ width: 'min(95vw, 600px)' }}
        footer={
          <div className="flex justify-end gap-2">
            <Button
              label="Cancel"
              icon="pi pi-times"
              onClick={() => {
                setShowPaymentDialog(false)
                setEditingPayment(null)
                setEditingOutgoingPayment(null)
              }}
              className="p-button-text"
            />
            <Button
              label={
                paymentView === 'incoming'
                  ? (editingPayment ? 'Update' : 'Record')
                  : (editingOutgoingPayment ? 'Update' : 'Record')
              }
              icon="pi pi-check"
              onClick={handleSavePayment}
              className="p-button-success"
            />
          </div>
        }
        onHide={() => {
          setShowPaymentDialog(false)
          setEditingPayment(null)
          setEditingOutgoingPayment(null)
        }}
      >
        <div className="space-y-4">
          {/* Incoming Payment Form */}
          {paymentView === 'incoming' && (
            <>
              {editingPayment && editingPayment.reference && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reference Number
                  </label>
                  <InputText
                    value={editingPayment.reference}
                    disabled
                    className="w-full"
                    style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
                  />
                  <small className="text-gray-500">Auto-generated reference (cannot be edited)</small>
                </div>
              )}

              <div>
                <label htmlFor="paymentType" className="block text-sm font-medium text-gray-700 mb-2">
                  Payment Type <span className="text-red-500">*</span>
                </label>
                <Dropdown
                  id="paymentType"
                  value={paymentFormData.paymentType}
                  options={paymentTypeOptions}
                  onChange={(e) => setPaymentFormData({ ...paymentFormData, paymentType: e.value })}
                  className="w-full"
                />
              </div>

              {paymentFormData.paymentType === PaymentType.MEMBERSHIP_FEE && (
                <>
                  <div>
                    <label htmlFor="member" className="block text-sm font-medium text-gray-700 mb-2">
                      Member <span className="text-red-500">*</span>
                    </label>
                    <Dropdown
                      id="member"
                      value={paymentFormData.memberId}
                      options={members.map(member => ({
                        label: `${member.firstName} ${member.lastName} (${member.memberNumber})`,
                        value: member.id
                      }))}
                      onChange={(e) => {
                        const selectedMember = members.find(m => m.id === e.value)
                        setPaymentFormData({
                          ...paymentFormData,
                          memberId: e.value,
                          amount: Number(selectedMember?.membershipFee) || 12.00
                        })
                      }}
                      placeholder="Select a member"
                      className="w-full"
                      filter
                      filterBy="label"
                      showClear
                    />
                  </div>

                  <div>
                    <label htmlFor="paymentYear" className="block text-sm font-medium text-gray-700 mb-2">
                      Payment Year <span className="text-red-500">*</span>
                    </label>
                    <InputNumber
                      id="paymentYear"
                      value={paymentFormData.paymentYear}
                      onValueChange={(e) => setPaymentFormData({ ...paymentFormData, paymentYear: e.value })}
                      className="w-full"
                      min={2020}
                      max={2030}
                      useGrouping={false}
                    />
                  </div>
                </>
              )}

              {paymentFormData.paymentType === PaymentType.DONATION && (
                <div>
                  <label htmlFor="member" className="block text-sm font-medium text-gray-700 mb-2">
                    Member (Optional)
                  </label>
                  <Dropdown
                    id="member"
                    value={paymentFormData.memberId}
                    options={members.map(member => ({
                      label: `${member.firstName} ${member.lastName} (${member.memberNumber})`,
                      value: member.id
                    }))}
                    onChange={(e) => setPaymentFormData({ ...paymentFormData, memberId: e.value })}
                    placeholder="Select a member (optional)"
                    className="w-full"
                    filter
                    filterBy="label"
                    showClear
                  />
                </div>
              )}

              <div>
                <label htmlFor="amount" className="block text-sm font-medium text-gray-700 mb-2">
                  Amount (€) <span className="text-red-500">*</span>
                </label>
                <InputNumber
                  id="amount"
                  value={paymentFormData.amount}
                  onValueChange={(e) => setPaymentFormData({ ...paymentFormData, amount: e.value || 0 })}
                  mode="decimal"
                  minFractionDigits={2}
                  maxFractionDigits={2}
                  min={0}
                  className="w-full"
                />
              </div>

              <div>
                <label htmlFor="receiveDate" className="block text-sm font-medium text-gray-700 mb-2">
                  Date Received <span className="text-red-500">*</span>
                </label>
                <Calendar
                  id="receiveDate"
                  value={paymentFormData.receiveDate}
                  onChange={(e) => setPaymentFormData({ ...paymentFormData, receiveDate: e.value || new Date() })}
                  className="w-full"
                  dateFormat="dd/mm/yy"
                />
              </div>

              <div>
                <label htmlFor="paymentMethod" className="block text-sm font-medium text-gray-700 mb-2">
                  Payment Method <span className="text-red-500">*</span>
                </label>
                <Dropdown
                  id="paymentMethod"
                  value={paymentFormData.paymentMethod}
                  options={paymentMethodOptions}
                  onChange={(e) => setPaymentFormData({ ...paymentFormData, paymentMethod: e.value })}
                  placeholder="Select payment method"
                  className="w-full"
                />
              </div>

              <div>
                <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-2">
                  Notes (Optional)
                </label>
                <InputTextarea
                  id="notes"
                  value={paymentFormData.notes}
                  onChange={(e) => setPaymentFormData({ ...paymentFormData, notes: e.target.value })}
                  rows={3}
                  className="w-full"
                  placeholder="Additional notes about this payment..."
                />
              </div>
            </>
          )}

          {/* Outgoing Payment Form */}
          {paymentView === 'outgoing' && (
            <>
              <div>
                <label htmlFor="payee" className="block text-sm font-medium text-gray-700 mb-2">
                  Payee <span className="text-red-500">*</span>
                </label>
                <InputText
                  id="payee"
                  value={outgoingPaymentFormData.payee}
                  onChange={(e) => setOutgoingPaymentFormData({ ...outgoingPaymentFormData, payee: e.target.value })}
                  className="w-full"
                  placeholder="Enter payee name"
                />
              </div>

              <div>
                <label htmlFor="outgoingAmount" className="block text-sm font-medium text-gray-700 mb-2">
                  Amount (€) <span className="text-red-500">*</span>
                </label>
                <InputNumber
                  id="outgoingAmount"
                  value={outgoingPaymentFormData.amount}
                  onValueChange={(e) => setOutgoingPaymentFormData({ ...outgoingPaymentFormData, amount: e.value || 0 })}
                  mode="decimal"
                  minFractionDigits={2}
                  maxFractionDigits={2}
                  min={0}
                  className="w-full"
                />
              </div>

              <div>
                <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-2">
                  Category <span className="text-red-500">*</span>
                </label>
                <Dropdown
                  id="category"
                  value={outgoingPaymentFormData.category}
                  options={outgoingCategoryOptions}
                  onChange={(e) => setOutgoingPaymentFormData({ ...outgoingPaymentFormData, category: e.value })}
                  className="w-full"
                  placeholder="Select category"
                />
              </div>

              <div>
                <label htmlFor="paymentDate" className="block text-sm font-medium text-gray-700 mb-2">
                  Payment Date <span className="text-red-500">*</span>
                </label>
                <Calendar
                  id="paymentDate"
                  value={outgoingPaymentFormData.paymentDate}
                  onChange={(e) => setOutgoingPaymentFormData({ ...outgoingPaymentFormData, paymentDate: e.value || new Date() })}
                  className="w-full"
                  dateFormat="dd/mm/yy"
                />
              </div>

              <div>
                <label htmlFor="outgoingPaymentMethod" className="block text-sm font-medium text-gray-700 mb-2">
                  Payment Method <span className="text-red-500">*</span>
                </label>
                <Dropdown
                  id="outgoingPaymentMethod"
                  value={outgoingPaymentFormData.paymentMethod}
                  options={paymentMethodOptions}
                  onChange={(e) => setOutgoingPaymentFormData({ ...outgoingPaymentFormData, paymentMethod: e.value })}
                  placeholder="Select payment method"
                  className="w-full"
                />
              </div>

              <div>
                <label htmlFor="invoiceNumber" className="block text-sm font-medium text-gray-700 mb-2">
                  Invoice Number (Optional)
                </label>
                <InputText
                  id="invoiceNumber"
                  value={outgoingPaymentFormData.invoiceNumber}
                  onChange={(e) => setOutgoingPaymentFormData({ ...outgoingPaymentFormData, invoiceNumber: e.target.value })}
                  className="w-full"
                  placeholder="Enter invoice number"
                />
              </div>

              <div>
                <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-2">
                  Status <span className="text-red-500">*</span>
                </label>
                <Dropdown
                  id="status"
                  value={outgoingPaymentFormData.status}
                  options={outgoingStatusOptions}
                  onChange={(e) => setOutgoingPaymentFormData({ ...outgoingPaymentFormData, status: e.value })}
                  className="w-full"
                  placeholder="Select status"
                />
              </div>

              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                  Description (Optional)
                </label>
                <InputTextarea
                  id="description"
                  value={outgoingPaymentFormData.description}
                  onChange={(e) => setOutgoingPaymentFormData({ ...outgoingPaymentFormData, description: e.target.value })}
                  rows={3}
                  className="w-full"
                  placeholder="Additional details about this payment..."
                />
              </div>
            </>
          )}
        </div>
      </Dialog>

      <BankImportWizard
        visible={showBankImport}
        onHide={() => {
          setShowBankImport(false)
          fetchData()
        }}
      />
    </div>
  )
}

export default PaymentDashboard
